import { useTranslation } from "react-i18next";
import { useEffect, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Typography,
} from "@mui/material";
import BusinessIcon from '@mui/icons-material/Business';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadImageButton from "./UploadImageButton";
import generateCloudinaryLink from "src/_utils/generateCloudinaryLink";
import { isValidBase64ImageString } from "src/_utils/base64Utils";

const isPdfDataSource = (file) => typeof file === 'string' && (
    file.startsWith('data:application/pdf') ||
    file.includes('data:application/pdf')
);

const isPdfUrl = (file) => typeof file === 'string' && (
    /^https?:\/\/.*\.pdf(?:\?.*)?$/i.test(file)
);

const isPdfSource = (file) => typeof file === 'string' && (
    isPdfDataSource(file) ||
    /\.pdf(?:$|\?)/i.test(file)
);

const isImageUrl = (file) => typeof file === 'string' && (
    /^https?:\/\/.*\.(jpeg|jpg|gif|png|bmp|webp|svg)(?:\?.*)?$/i.test(file)
);

export default function UploadImageCard({
    name,
    title = "Choose image",
    handleChange,
    image,
    error,
    variant,
    imgProps,
    isImageClickable = true,
    accept,
    ...props
}) {
    const { t } = useTranslation();

    const [source, setSource] = useState();

    useEffect(() => {
        if (!image) {
            setSource(undefined)
            return
        }

        if (isValidBase64ImageString(image) || isPdfDataSource(image)) {
            setSource(image)
        } else if (isImageUrl(image) || isPdfUrl(image)) {
            return setSource(image)
        } else {
            setSource(
                generateCloudinaryLink(image)
            )
        }
    }, [image])

    const [openImage, setOpenImage] = useState(false)
    const isPdf = isPdfSource(source)
    const canPreviewFile = !!source && isImageClickable

    const openFileInNewTab = () => {
        if (!source) return

        if (source.startsWith('data:application/pdf')) {
            const base64Data = source.split(',')[1]

            if (base64Data) {
                const byteArray = Uint8Array.from(atob(base64Data), char => char.charCodeAt(0))
                const blob = new Blob([byteArray], { type: 'application/pdf' })
                const url = URL.createObjectURL(blob)
                window.open(url, '_blank', 'noopener,noreferrer')
                setTimeout(() => URL.revokeObjectURL(url), 1000)
                return
            }
        }

        window.open(source, '_blank', 'noopener,noreferrer')
    }

    return (
        <>
            <Dialog
                maxWidth={isPdf ? 'lg' : 'sm'}
                fullWidth={isPdf}
                onClose={() => setOpenImage(false)}
                open={openImage && !!source}
            >
                {isPdf ? (
                    <>
                        <DialogContent sx={{ height: '80vh', p: 0 }}>
                            <iframe
                                src={source}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                title={t('PDF Document')}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button startIcon={<OpenInNewIcon />} onClick={openFileInNewTab}>
                                {t('Open in New Tab')}
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <img src={source} style={{ objectFit: 'contain' }} alt={title} />
                )}
            </Dialog>
            <Card {...props}>
                <CardContent>
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Avatar
                            src={isPdf ? undefined : source}
                            sx={{
                                height: 100,
                                width: 100,
                                cursor: canPreviewFile ? 'pointer' : '',
                                bgcolor: isPdf ? 'grey.100' : undefined,
                                color: isPdf ? 'error.main' : undefined,
                            }}
                            variant={variant}
                            imgProps={imgProps}
                            onClick={() => {
                                if (canPreviewFile) {
                                    setOpenImage(true)
                                }
                            }}
                        >
                            {isPdf ? <PictureAsPdfIcon sx={{ fontSize: 64 }} /> : <BusinessIcon sx={{ fontSize: 100 }} />}
                        </Avatar>
                        {
                            !!error &&
                            <Typography color='error' mx={1} variant="caption" sx={{ textAlign: "center" }}>
                                {error}
                            </Typography>
                        }
                    </Box>
                </CardContent>
                <Divider />
                <CardActions
                    sx={{
                        flexDirection: isPdf ? 'column' : 'row',
                        alignItems: 'stretch',
                        '& > :not(:first-of-type)': {
                            ml: isPdf ? 0 : undefined,
                            mt: isPdf ? 1 : undefined,
                        },
                    }}
                >
                    {/* {isPdf && (
                        <Button
                            startIcon={<OpenInNewIcon />}
                            onClick={openFileInNewTab}
                            sx={{ fontWeight: 'bold', textAlign: "center", textTransform: "none" }}
                            fullWidth
                        >
                            {t('Open in New Tab')}
                        </Button>
                    )} */}
                    <UploadImageButton
                        title={title}
                        handleChange={handleChange}
                        name={name}
                        accept={accept}
                        sx={{ fontWeight: 'bold', textAlign: "center", textTransform: "none" }}
                        fullWidth
                    />
                </CardActions>
            </Card>
        </>
    );
};
